import { useState } from "react";
import { IconButton } from "@/components/common/IconButton";
import { stopChatSubagentRun } from "@/lib/api";
import {
	isTerminalSubagentStatus,
	stopResultToast,
	type ChatSubagentRun,
	type SubagentRunStatus,
} from "@/lib/chat";
import { useChatStore } from "@/stores/useChatStore";
import { useCurrentUserStore } from "@/stores/useCurrentUserStore";
import { useToastStore } from "@/stores/useToastStore";
import { canStopRun } from "./run-stop-permission";

/** background サブエージェント実行の停止操作と、その表示可否を担当する。 */
export function SubagentStopButton({ run }: { run: ChatSubagentRun }) {
	const chatId = useChatStore((s) => s.chatId);
	// 同じ run にボタンが複数描かれる (一覧カードと詳細ヘッダ) ため、状態は一覧を単一の情報源と
	// する。詳細ヘッダの run は別ポーリング由来で遅れることがあり、片方だけ押せる状態になる。
	const storedStatus = useChatStore(
		(s) => s.subagentRuns?.find((entry) => entry.id === run.id)?.status,
	);
	const updateSubagentRunStatus = useChatStore((s) => s.updateSubagentRunStatus);
	const currentUserId = useCurrentUserStore((s) => s.id);
	const role = useCurrentUserStore((s) => s.role);
	// 停止要求の結果を保持し、一覧のポーリング (最大 2 秒) を待たずに表示へ反映する。
	const [requestedStatus, setRequestedStatus] = useState<SubagentRunStatus>();
	const [isStopping, setIsStopping] = useState(false);

	// 停止 API は background 限定。foreground は親ジョブの停止で終了するため対象外。
	if (!run.isBackground || chatId === null) return null;
	const latestStatus = storedStatus ?? run.status;
	// 停止要求と正常終了が競合した場合は、先に確定した終端状態を優先する。
	const status = isTerminalSubagentStatus(latestStatus)
		? latestStatus
		: (requestedStatus ?? latestStatus);
	if (status === "cancelRequested") {
		return (
			<span data-testid="subagent-stop-pending" className="shrink-0 text-caption text-fg-3">
				停止中…
			</span>
		);
	}
	if (isTerminalSubagentStatus(status)) return null;
	if (!canStopRun(role, currentUserId, run.launchedByUserId)) return null;

	const handleStop = async () => {
		setIsStopping(true);
		try {
			const result = await stopChatSubagentRun(chatId, run.id);
			setRequestedStatus(result.status);
			updateSubagentRunStatus(run.id, result.status);
			useToastStore.getState().show(stopResultToast(run.description, result.status));
		} catch (error) {
			useToastStore.getState().show({
				type: "error",
				message: error instanceof Error ? error.message : "サブエージェントの停止に失敗しました。",
			});
			throw error;
		} finally {
			setIsStopping(false);
		}
	};

	return (
		<IconButton
			icon="stop"
			size={20}
			iconSize={12}
			color="var(--fg-1)"
			background="var(--surface-2)"
			disabled={isStopping}
			data-tooltip={isStopping ? "停止を要求しています…" : "サブエージェントを停止"}
			data-testid="subagent-stop"
			// 失敗は handleStop がトーストで伝えたうえで再スローする。ここで受けないと未処理拒否になる。
			onClick={() => void handleStop().catch(() => {})}
		/>
	);
}
